import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { brandService } from '@/lib/brandService';
import { useWorkspace } from './WorkspaceContext';
import { runFullBrandMigration } from '@/lib/brandMigration';
import { migrateBrandColorSection } from '@/lib/migrateBrandColorSection';
import { migrateColorPalettesSection } from '@/lib/migrateColorPalettesSection';
import { migrateMoodboardGallerySection } from '@/lib/migrateMoodboardGallerySection';

export interface BrandData {
  name?: string;
  industry?: string;
  personality?: string[];
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background?: string;
    title?: string;
    text?: string;
  };
  typography: {
    primary: string;
    secondary: string;
  };
  mission?: string;
  vision?: string;
  values?: string[];
  targetAudience?: string[];
  selectedColorPreset?: any;
  moodboardImages?: string[];
  brandLogo?: string; // Optional brand logo URL
  brandThumbnail?: string; // Optional brand thumbnail URL for dashboard
  brandDescription?: string; // Brand description field
  brandImage?: string; // Brand image field
  brandStoryDescription?: string; // NEW: Brand story description field
  brandStoryImage?: string; // NEW: Brand story image field
  brandUrl?: string; // Brand website URL
  primaryDarkLogo?: string; // Primary dark logo URL
  favicon?: string; // Brand favicon URL
  globalTitleFont?: string; // Global title font
  globalTextFont?: string; // Global text font
  globalTitleFontColor?: string; // Global title font color
  globalTextFontColor?: string; // Global text font color
  designCredit?: string; // Design credit text
}

interface SectionCustomization {
  backgroundType: 'color' | 'image';
  backgroundColor: string;
  backgroundImage: string;
  backgroundPosition: string;
  backgroundSize: string;
  backgroundRepeat: string;
  overlayEnabled: boolean;
  overlayColor: string;
  overlayOpacity: number;
  overlayBlendMode: string;
}

interface Section {
  id: string;
  title: string;
  content: any;
  styles: {
    backgroundColor: string;
    textColor: string;
    fontFamily: string;
    fontSize: string;
    padding?: string;
    paddingTop?: string;
    paddingRight?: string;
    paddingBottom?: string;
    paddingLeft?: string;
    backgroundImage?: string;
    backgroundPosition?: string;
    backgroundSize?: string;
    backgroundRepeat?: string;
    position?: string;
  };
  customization?: SectionCustomization;
}

interface BrandState {
  brandData: BrandData | null;
  sections: Section[];
  currentSection: number;
  history: Section[][];
  historyIndex: number;
  savedBrands: any[];
  currentBrandId: string | null;
  isLoading: boolean;
}

type BrandAction =
  | { type: 'SET_BRAND_DATA'; payload: BrandData }
  | { type: 'SET_SECTIONS'; payload: Section[] }
  | { type: 'UPDATE_SECTION'; payload: { index: number; section: Section } }
  | { type: 'SET_CURRENT_SECTION'; payload: number }
  | { type: 'SET_CURRENT_BRAND_ID'; payload: string | null }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'SAVE_BRAND'; payload: any }
  | { type: 'LOAD_SAVED_BRANDS'; payload: any[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'RESET_BRAND' };

const initialState: BrandState = {
  brandData: {
    name: 'Brand Name',
    industry: 'Technology',
    personality: ['Professional', 'Innovative'],
    colors: {
      primary: '#3B82F6',
      secondary: '#10B981',
      accent: '#F59E0B'
    },
    typography: {
      primary: 'Inter',
      secondary: 'Inter'
    },
    mission: 'To provide innovative solutions',
    vision: 'To be the leading provider',
    values: ['Innovation', 'Quality', 'Customer Focus'],
    brandLogo: '',
    brandThumbnail: '',
    brandDescription: 'A technology company focused on innovation',
    brandImage: '',
    brandStoryDescription: 'Our journey in technology',
    brandStoryImage: '',
    brandUrl: 'brand.com',
    primaryDarkLogo: '',
    globalTitleFont: 'Inter',
    globalTextFont: 'Inter',
    globalTitleFontColor: '#000000',
    globalTextFontColor: '#333333',
    designCredit: ''
  },
  sections: [
    {
      id: 'guidelines',
      title: 'Brand Style Guidelines',
      content: {
        brandBookTitle: 'BrandBook',
        currentDate: `Date: ${new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`,
        brandGuideline: '{{brandName}}/Brand Guideline',
        designCredit: 'Designed by {{designCredit}}',
        brandName: 'Brand Name',
        brandLogo: '',
        brandBookTitleStyles: {
          fontFamily: 'Inter',
          color: '#FFFFFF'
        },
        brandGuidelineStyles: {
          fontFamily: 'Helvetica',
          color: '#FFFFFF'
        },
        designCreditStyles: {
          textAlign: 'right',
          fontFamily: 'Helvetica',
          color: '#FFFFFF'
        }
      },
      styles: {
        backgroundColor: '#C8FF00',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '48px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#C8FF00',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'table-of-contents',
      title: 'Table of Contents',
      content: {
        tocTitle: 'Table of Content',
        brandName: 'Brand Name',
        tocImage: '/img-placeholder-16-9.png',
        listTitle: 'CONTENTS',
        // Updated table of contents items - 6 items with editable numbers
        item1Title: 'Introduction',
        item1Number: '01',
        item2Title: 'Logo',
        item2Number: '02',
        item3Title: 'Color',
        item3Number: '03',
        item4Title: 'Typography',
        item4Number: '04',
        item5Title: 'Moodboard',
        item5Number: '05',
        item6Title: 'Thank You',
        item6Number: '06',
        // Bottom section content
        brandGuideline: '{{brandName}}/Brand Guideline',
        brandUrl: 'visit: {{brandUrl}}',
        brandGuidelineStyles: { 
          fontFamily: 'Helvetica',
          color: '#000000'
        },
        brandUrlStyles: { 
          textAlign: 'right', 
          fontFamily: 'Helvetica',
          color: '#000000'
        }
      },
      styles: {
        backgroundColor: '#F8F9FA',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '18px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#F8F9FA',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'introduction',
      title: 'Brand Introduction',
      content: {
        introTitle: 'Brand Introduction',
        brandDescription: 'Tell us about brand\'s story and purpose',
        brandGuideline: '{{brandName}}/Brand Guideline',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandUrl: 'visit: {{brandUrl}}',
        brandName: 'Brand Name',
        brandImage: '/brand-intro.jpg'
      },
      styles: {
        backgroundColor: '#F8F9FA',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '18px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#F8F9FA',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'brand-story',
      title: 'Brand Story',
      content: {
        storyTitle: 'Brand Story',
        brandStoryDescription: 'Tell brand\'s unique story here. Share the journey, values, and vision that make brand special...',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandGuideline: '{{brandName}}/Brand Guideline',
        brandUrl: 'visit: {{brandUrl}}',
        storyImage: '/brand-story.jpg'
      },
      styles: {
        backgroundColor: '#F8F9FA',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '18px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#F8F9FA',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'mission-vision',
      title: 'Mission & Vision',
      content: {
        brandName: 'Brand Name',
        sectionTitle: 'Mission & Vision',
        missionTitle: 'Our Mission',
        mission: 'Brand Name specializes in delivering innovative solutions that drive growth and create lasting value for our clients.',
        visionTitle: 'Our Vision',
        vision: 'Brand Name provides solutions for the sustainable and efficient operation of modern business platforms.',
        visionImage: '/img-placeholder-16-9.png',
        brandGuideline: '{{brandName}}/Brand Guideline',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandUrl: 'visit: {{brandUrl}}'
      },
      styles: {
        backgroundColor: '#F9FAFB',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '18px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#F9FAFB',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'target-audience',
      title: 'Target Audience',
      content: {
        sectionTitle: 'Target Audience',
        description: 'Define the key demographic groups and personas that your brand aims to reach and engage with.',
        audiences: JSON.stringify(['Young Professionals', 'Students', 'Entrepreneurs', 'Creatives']),
        brandGuideline: '{{brandName}}/Brand Guideline',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandUrl: 'visit: {{brandUrl}}'
      },
      styles: {
        backgroundColor: '#FFFFFF',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '18px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#FFFFFF',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'brand-logo-cover',
      title: 'Brand Logo Cover',
      content: {
        coverTitle: 'LOGO',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandGuideline: '{{brandName}}/Brand Guideline',
        designCredit: 'Designed By {{designCredit}}',
        brandLogo: ''
      },
      styles: {
        backgroundColor: '#3B82F6',
        textColor: '#FFFFFF',
        fontFamily: 'Helvetica',
        fontSize: '48px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#3B82F6',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'brand-logo',
      title: 'Brand Logo',
      content: {
        logoTitle: 'Brand Logo',
        logoDescription: 'Brand logo represents our brand identity and values',
        brandGuideline: '{{brandName}}/Brand Guideline',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandUrl: 'visit: {{brandUrl}}',
        brandName: 'Brand Name',
        primaryLogo: '/img-placeholder-16-9.png',
        originalLogo: '/img-placeholder-16-9.png',
        lightLogo: '/img-placeholder-16-9.png',
        darkLogo: '/img-placeholder-16-9.png'
      },
      styles: {
        backgroundColor: '#F8F9FA',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '18px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#F8F9FA',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'brand-favicon',
      title: 'Favicon',
      content: {
        faviconTitle: 'Favicon',
        faviconDescription: 'Favicon represents our brand in browser tabs and bookmarks',
        brandGuideline: '{{brandName}}/Brand Guideline',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandUrl: 'visit: {{brandUrl}}',
        brandName: 'Brand Name',
        primaryFavicon: '/img-placeholder-16-9.png',
        originalFavicon: '/img-placeholder-16-9.png',
        lightFavicon: '/img-placeholder-16-9.png',
        darkFavicon: '/img-placeholder-16-9.png'
      },
      styles: {
        backgroundColor: '#F8F9FA',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '18px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#F8F9FA',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'do-dont',
      title: 'Logo Usage',
      content: {
        mainTitle: 'Logo Usage',
        usageDescription: 'These examples demonstrate common mistakes to avoid when using the logo. Following these guidelines ensures consistent brand representation.',
        brandGuideline: '{{brandName}}/Brand Guideline',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandUrl: 'visit: {{brandUrl}}',
        brandName: 'Brand Name',
        // Image placeholders for 3x2 grid
        usage1: '/img-placeholder-16-9.png',
        usage1Label: 'Best Practice 1',
        usage2: '/img-placeholder-16-9.png',
        usage2Label: 'Best Practice 2',
        usage3: '/img-placeholder-16-9.png',
        usage3Label: 'Best Practice 3',
        usage4: '/img-placeholder-16-9.png',
        usage4Label: 'Best Practice 4',
        usage5: '/img-placeholder-16-9.png',
        usage5Label: 'Best Practice 5',
        usage6: '/img-placeholder-16-9.png',
        usage6Label: 'Best Practice 6'
      },
      styles: {
        backgroundColor: '#F8F9FA',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '18px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#F8F9FA',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'color-cover',
      title: 'Color Cover',
      content: {
        coverTitle: 'COLOR',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandGuideline: '{{brandName}}/Brand Guideline',
        designCredit: 'Designed By {{designCredit}}',
        brandLogo: ''
      },
      styles: {
        backgroundColor: '#3B82F6',
        textColor: '#FFFFFF',
        fontFamily: 'Helvetica',
        fontSize: '48px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#3B82F6',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'brand-colors',
      title: 'Brand Colors',
      content: {
        colorsTitle: 'Brand Colors',
        colorsDescription: 'Our carefully curated color palette reflects our brand identity and creates a cohesive visual experience.',
        brandGuideline: '{{brandName}}/Brand Guideline',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandUrl: 'visit: {{brandUrl}}',
        brandName: 'Brand Name'
      },
      styles: {
        backgroundColor: '#F8F9FA',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '18px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#F8F9FA',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'color-palettes',
      title: 'Color Palettes',
      content: {
        paletteTitle: 'Color Palette',
        paletteDescription: 'Our color palette includes carefully crafted shades and tints for each brand color, providing flexibility for various design applications.',
        brandGuideline: '{{brandName}}/Brand Guideline',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandUrl: 'visit: {{brandUrl}}',
        brandName: 'Brand Name'
      },
      styles: {
        backgroundColor: '#F8F9FA',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '18px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#F8F9FA',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'typography-cover',
      title: 'Typography Cover',
      content: {
        coverTitle: 'TYPOGRAPHY',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandGuideline: '{{brandName}}/Brand Guideline',
        designCredit: 'Designed By {{designCredit}}',
        brandLogo: ''
      },
      styles: {
        backgroundColor: '#3B82F6',
        textColor: '#FFFFFF',
        fontFamily: 'Helvetica',
        fontSize: '48px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#3B82F6',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'typography-fonts',
      title: 'Typography Fonts',
      content: {
        headingTitle: 'Heading Font',
        headingDescription: 'Use the heading font for page titles, section headers, and important headlines. It establishes visual hierarchy and captures attention with its distinctive character.',
        headingWeights: 'Font Weights: 400, 500, 700.',
        bodyTitle: 'Body Font',
        bodyDescription: 'Apply the body font for paragraphs, descriptions, captions, and longer text blocks. It ensures optimal readability and maintains consistency across all content.',
        bodyWeights: 'Font Weights: 400, 500, 700.'
      },
      styles: {
        backgroundColor: '#FFFFFF',
        textColor: '#000000',
        fontFamily: 'Helvetica',
        fontSize: '16px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#FFFFFF',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'photography-cover',
      title: 'Moodboard Cover',
      content: {
        coverTitle: 'MOODBOARD',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandGuideline: '{{brandName}}/Brand Guideline',
        designCredit: 'Designed By {{designCredit}}',
        brandLogo: ''
      },
      styles: {
        backgroundColor: '#3B82F6',
        textColor: '#FFFFFF',
        fontFamily: 'Helvetica',
        fontSize: '48px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#3B82F6',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'moodboard-gallery',
      title: 'Moodboard Gallery',
      content: {
        galleryTitle: 'Moodboard',
        galleryImages: JSON.stringify([])
      },
      styles: {
        backgroundColor: '#FFFFFF',
        textColor: '#333942',
        fontFamily: 'Helvetica',
        fontSize: '16px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#FFFFFF',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    },
    {
      id: 'thank-you-cover',
      title: 'Thank You Cover',
      content: {
        coverTitle: 'THANK YOU',
        publishedDate: 'Published Date: {{publishedDate}}',
        brandGuideline: '{{brandName}}/Brand Guideline',
        designCredit: 'Designed By {{designCredit}}',
        brandLogo: ''
      },
      styles: {
        backgroundColor: '#3B82F6',
        textColor: '#FFFFFF',
        fontFamily: 'Helvetica',
        fontSize: '48px',
        padding: '60px',
        position: 'relative'
      },
      customization: {
        backgroundType: 'color',
        backgroundColor: '#3B82F6',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 0,
        overlayBlendMode: 'normal'
      }
    }
  ],
  currentSection: 0,
  history: [],
  historyIndex: -1,
  savedBrands: [],
  currentBrandId: null,
  isLoading: false
};

const brandReducer = (state: BrandState, action: BrandAction): BrandState => {
  switch (action.type) {
    case 'SET_BRAND_DATA':
      return { ...state, brandData: action.payload };
    
    case 'SET_SECTIONS':
      const newHistory = state.history.slice(0, state.historyIndex + 1);
      return {
        ...state,
        sections: action.payload,
        history: [...newHistory, action.payload],
        historyIndex: newHistory.length
      };
    
    case 'UPDATE_SECTION':
      const updatedSections = [...state.sections];
      updatedSections[action.payload.index] = action.payload.section;
      const updateHistory = state.history.slice(0, state.historyIndex + 1);
      return {
        ...state,
        sections: updatedSections,
        history: [...updateHistory, updatedSections],
        historyIndex: updateHistory.length
      };
    
    case 'SET_CURRENT_SECTION':
      return { ...state, currentSection: action.payload };
    
    case 'SET_CURRENT_BRAND_ID':
      return { ...state, currentBrandId: action.payload };
    
    case 'UNDO':
      if (state.historyIndex > 0) {
        return {
          ...state,
          sections: state.history[state.historyIndex - 1],
          historyIndex: state.historyIndex - 1
        };
      }
      return state;
    
    case 'REDO':
      if (state.historyIndex < state.history.length - 1) {
        return {
          ...state,
          sections: state.history[state.historyIndex + 1],
          historyIndex: state.historyIndex + 1
        };
      }
      return state;
    
    case 'SAVE_BRAND':
      const savedBrands = [...state.savedBrands, action.payload];
      return { ...state, savedBrands };

    case 'LOAD_SAVED_BRANDS':
      return { ...state, savedBrands: action.payload };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'RESET_BRAND':
      return {
        ...initialState,
        savedBrands: state.savedBrands,
      };

    default:
      return state;
  }
};

const BrandContext = createContext<{
  state: BrandState;
  dispatch: React.Dispatch<BrandAction>;
} | null>(null);

export const BrandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(brandReducer, initialState);
  const { currentWorkspace } = useWorkspace();
  const [searchParams] = useSearchParams();

  // Listen to brandId in URL for deep linking
  useEffect(() => {
    const brandId = searchParams.get('brandId');
    if (brandId && brandId !== state.currentBrandId) {
      dispatch({ type: 'SET_CURRENT_BRAND_ID', payload: brandId });
    }
  }, [searchParams, state.currentBrandId]);

  // Load brands from database when workspace changes
  useEffect(() => {
    const loadBrands = async () => {
      if (!currentWorkspace) {
        dispatch({ type: 'LOAD_SAVED_BRANDS', payload: [] });
        return;
      }

      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const brands = await brandService.getBrandsByWorkspace(currentWorkspace.id);
        const mappedBrands = brands.map((brand) => ({
          id: brand.id,
          name: brand.name,
          status: brand.status,
          createdAt: brand.created_at,
          brandData: {
            name: brand.name,
            industry: brand.industry,
            personality: brand.personality,
            colors: brand.colors,
            typography: brand.typography,
            mission: brand.mission,
            vision: brand.vision,
            values: brand.values,
            targetAudience: brand.target_audience || [],
            moodboardImages: brand.moodboard_images || [],
            brandLogo: brand.logo_url || '',
            brandThumbnail: brand.thumbnail_url || '',
            brandDescription: brand.description || '',
            brandImage: brand.image_url || '',
            brandStoryDescription: brand.story_description || '',
            favicon: brand.favicon_url || '',
            brandStoryImage: brand.story_image_url || '',
            brandUrl: brand.website_url || '',
            primaryDarkLogo: brand.dark_logo_url || '',
            globalTitleFont: brand.global_title_font || '',
            globalTextFont: brand.global_text_font || '',
            globalTitleFontColor: brand.global_title_font_color || '',
            globalTextFontColor: brand.global_text_font_color || '',
          },
          sections: brand.sections || [],
        }));
        dispatch({ type: 'LOAD_SAVED_BRANDS', payload: mappedBrands });
      } catch (error) {
        console.error('Error loading brands:', error);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    loadBrands();
  }, [currentWorkspace?.id]);

  // Optimized Instant Load: Load specific brand if currentBrandId is set
  useEffect(() => {
    const loadCurrentBrand = async () => {
      if (!state.currentBrandId) return;

      const cachedBrand = state.savedBrands.find(b => String(b.id) === String(state.currentBrandId));
      if (cachedBrand) {
        // Only update state if the data is actually different to prevent flicker
        if (state.brandData?.name !== cachedBrand.brandData.name || state.sections.length !== cachedBrand.sections.length) {
          dispatch({ type: 'SET_BRAND_DATA', payload: cachedBrand.brandData });
          
          const fakeRawBrand = {
            colors: cachedBrand.brandData.colors,
            sections: cachedBrand.sections
          };
          const migratedSections = runFullBrandMigration({
            ...fakeRawBrand,
            sections: migrateMoodboardGallerySection(migrateColorPalettesSection(migrateBrandColorSection(cachedBrand.sections)))
          });
          
          dispatch({ type: 'SET_SECTIONS', payload: migratedSections });
        }
        // Success from cache!
        return;
      }

      // 2. Network Load: Fetch from database (e.g., on first reload or deep link)
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const brand = await brandService.getBrandById(state.currentBrandId);
        if (brand) {
          const brandData = {
            name: brand.name,
            industry: brand.industry,
            personality: brand.personality,
            colors: brand.colors,
            typography: brand.typography,
            mission: brand.mission,
            vision: brand.vision,
            values: brand.values,
            targetAudience: brand.target_audience || [],
            moodboardImages: brand.moodboard_images || [],
            brandLogo: brand.logo_url || '',
            brandThumbnail: brand.thumbnail_url || '',
            brandDescription: brand.description || '',
            brandImage: brand.image_url || '',
            brandStoryDescription: brand.story_description || '',
            brandStoryImage: brand.story_image_url || '',
            brandUrl: brand.website_url || '',
            primaryDarkLogo: brand.dark_logo_url || '',
            favicon: brand.favicon_url || '',
            globalTitleFont: brand.global_title_font || '',
            globalTextFont: brand.global_text_font || '',
            globalTitleFontColor: brand.global_title_font_color || '',
            globalTextFontColor: brand.global_text_font_color || '',
          };
          
          dispatch({ type: 'SET_BRAND_DATA', payload: brandData });
          
          // Multi-stage migration to ensure it meets the 18-section standard + structural fixes
          const brandWithInitialMigrations = {
            ...brand,
            sections: migrateMoodboardGallerySection(migrateColorPalettesSection(migrateBrandColorSection(brand.sections)))
          };
          const migratedSections = runFullBrandMigration(brandWithInitialMigrations);
          dispatch({ type: 'SET_SECTIONS', payload: migratedSections });
        }
      } catch (error) {
        console.error('Error loading current brand:', error);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    loadCurrentBrand();
  }, [state.currentBrandId, state.savedBrands]);

  return (
    <BrandContext.Provider value={{ state, dispatch }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};